// The studio's own record: what it is called, how to reach it, where it is,
// and how it writes people's names.
//
// It is stored at `users/{uid}/settings/studio` — one document, not a
// collection — the path the message sender reads (`getStudio` in
// functions/delivery/firestoreStore.js, `studioNameFor` in
// functions/delivery/facts.js, which fall back to the deployment's from-name
// when the document is absent), so the path and the field name `name` are a
// contract.
//
// Pure, like the rest of domain/ — the same normalisation runs in a Cloud
// Function or a test with no browser.
//
// ── What MMS does ──────────────────────────────────────────────────────────
// Business Settings → General shows a profile card (logo, studio name, region,
// time zone) with an "Edit Business Profile" button. That button opens a drawer
// with tabs General and Social Links, whose General tab holds, in order: Logo
// ("This logo will appear on all business reports such as invoices"), Studio
// Name (defaulted to the teacher's own name), Phone Number (Optional), Studio
// Address (Optional, a textarea), Region, and Time Zone. Save is disabled until
// something changes.
//
// **Currency is not a setting.** Accounts & Invoices has no currency field
// anywhere; Region is the only place currency is decided, and its hint says so:
// "Your selection controls currency, mileage log format, and date/time format".
// So this record stores a locale and nothing else about money; a currency
// field would be a second answer to a question Region already answers.
import { coreSync } from './core.js';

// How a person's name is written. MMS calls this Naming Format and offers the
// same three choices separately for Teacher, Student and Parent — which is
// where its "Nguyen, Ava" rendering comes from.
export const NAME_FORMATS = {
  lastCommaFirst: 'last_comma_first',
  firstLast: 'first_last',
  lastFirst: 'last_first',
};

// The labels MMS puts on the radios, in MMS's order.
export const NAME_FORMAT_OPTIONS = [
  { id: NAME_FORMATS.lastCommaFirst, label: 'Last, First' },
  { id: NAME_FORMATS.firstLast, label: 'First Last' },
  { id: NAME_FORMATS.lastFirst, label: 'Last First' },
];

// The three audiences MMS lets you set separately.
export const NAME_ROLES = ['teacher', 'student', 'parent'];

// MMS's defaults: teacher First Last, student and parent Last, First.
export const DEFAULT_NAME_FORMATS = Object.freeze({
  teacher: NAME_FORMATS.firstLast,
  student: NAME_FORMATS.lastCommaFirst,
  parent: NAME_FORMATS.lastCommaFirst,
});

// A locale identifier, not a currency and not a date pattern.
//
// Currency and date/time formatting are *derived* from this — see
// src/domain/locale.js (`currencyFor(locale)` and friends), which owns that
// derivation. This module owns the stored value and nothing else, so there is
// one answer to "what currency is this studio in".
//
// en-US, matching the USD default of every money call site (domain/money.js).
//
// **This is what a document that does not say reads as, not what a new studio
// starts with.** Sign-up stores the browser's region (#462, `signupLocale` in
// ./locale.js); this stays the answer for a studio with no
// document, on every device, so that it cannot differ between two of them.
export const DEFAULT_LOCALE = 'en-US';

// There is no default time zone here.
//
// The right one is a property of the teacher's machine, and a guess in the
// domain would make the same document normalise differently in a test, a
// Cloud Function and a browser. Null means "never set", and the UI offers the
// browser's resolved zone as the value to save — see src/lib/intlOptions.js.
export const DEFAULT_TIME_ZONE = null;

// What an invoice, a report or a receipt calls a studio that has not named
// itself. Matches functions/delivery/facts.js, which falls back to the same
// words after the deployment's from-name.
export const UNNAMED_STUDIO = 'Your Studio';

// ─── Settings groups ───────────────────────────────────────────────────────
//
// MMS spreads these across its Accounts & Invoices, Email & SMS and Policies
// tabs. They are declared here, together, as the vocabulary the panels that
// read them share.

// What an invoice shows when the family is in credit. MMS offers exactly these
// two and defaults to the first.
export const NEGATIVE_INVOICE_DISPLAYS = {
  zeroDue: 'zero_due',
  balanceRemaining: 'balance_remaining',
};

// The date a balance is struck on by default. MMS's four, in its order.
export const BALANCE_DATE_MODES = {
  today: 'today',
  endOfMonth: 'end_of_month',
  dayOfMonth: 'day_of_month',
  specificDate: 'specific_date',
};

// Ours, not MMS's #0895D6: the cyan the rest of the product uses.
export const DEFAULT_ACCENT_COLOR = '#0e7490';

export const DEFAULT_INVOICING = Object.freeze({
  // Falls back to the studio name when null. MMS keeps these separate so an
  // invoice can be issued under a trading name.
  invoiceName: null,
  footerText: null,
  accentColor: DEFAULT_ACCENT_COLOR,
  // One line per student rather than per lesson, for families with several.
  groupChargesByStudent: false,
  showLocation: false,
  negativeDisplay: NEGATIVE_INVOICE_DISPLAYS.zeroDue,
  balanceDateMode: BALANCE_DATE_MODES.today,
  // Only read when balanceDateMode is day_of_month. 1-28: 29 to 31 do not
  // exist in every month, and a billing date that silently skips February is
  // worse than one a studio has to pick again.
  balanceDayOfMonth: 1,
  // Only read when balanceDateMode is specific_date. A date-only string on the
  // UTC grid, like every other date in this app.
  balanceSpecificDate: null,
});

// How the studio identifies itself on outgoing mail.
//
// This is the studio-facing half of what functions/delivery/transport/
// sendgrid.js does with deployment env vars: a free-provider address cannot be
// the envelope From without failing DMARC, so the send goes out as the
// deployment's verified sender with the studio's name in the display name and
// Reply-To pointing back at the teacher. MMS surfaces that as a setting with a
// live From/Reply-To preview.
export const DEFAULT_MESSAGING = Object.freeze({
  studioEmail: null,
  // False, because on a new MMS studio every checkbox under Business Settings
  // → Email & SMS → Email Settings is unticked.
  useStudioNameAsSender: false,
  useStudioIdentityOnReminders: false,
  useStudioIdentityOnLessonNotes: false,

  // ── The invoice reminder ─────────────────────────────────────────────────
  //
  // MMS, Business Settings → Accounts & Invoices, "Notifications & Reminders":
  //
  //   [ ] Send an overdue invoice reminder        (unticked on a new studio)
  //         ↳ ticking it reveals exactly one field:
  //           "Enter the number of days after invoice due date to send
  //            overdue reminder"   default 0
  //
  // So it is off by default, and there is *one* reminder, N days past due, not
  // a cadence.
  //
  // `invoiceOverdueDays` lives in `messaging` rather than `invoicing` because
  // the worker reads this group and nothing else. MMS files it on the
  // Accounts & Invoices tab; the stored shape does not follow the panel.
  sendInvoiceReminders: false,
  invoiceOverdueDays: 0,

  // ── Whether this studio texts its families about money at all ────────────
  //
  // MMS, the same section, and the row *above* the overdue reminder:
  //
  //   [ ] Send SMS invoice notifications and invoice reminders
  //   [ ] Send an overdue invoice reminder
  //       Email Timeframe …
  //
  // The channel is a **studio-level decision of its own** (#91): turning the
  // overdue reminder on gets email, and a text as well is a second act by the
  // teacher. It is not a per-family fact — a family's `smsCapable` is the
  // *address*, not consent to use it for this. Off by default, as in MMS.
  //
  // MMS's wording covers invoice **notifications as well as reminders**: one
  // question about texting families about invoices at all, hence
  // `sendInvoiceSms` rather than `sendInvoiceReminderSms`. It gates the
  // `invoice_reminder` text (domain/reminders.js); the `invoice` kind (#622)
  // is sent by email only.
  sendInvoiceSms: false,

  // ── The Email Timeframe ──────────────────────────────────────────────────
  //
  // MMS, same section, the row below the overdue reminder:
  //
  //   Email Timeframe
  //   Between 8:00 AM and 9:00 AM
  //   "Set what time your auto-invoices & automatic invoice reminder emails
  //    will be sent"
  //
  // The worker wakes every hour of the day; this keeps an overdue invoice from
  // being chased at eleven at night. The hour is read on the studio's own
  // clock (`studioHour` in domain/reminders.js), and an invoice reminder that
  // falls due outside it waits for the window.
  //
  // **One stored hour, and the end is derived.** MMS's Email Timeframe is a
  // *single* select whose twenty-four options are consecutive one-hour windows
  // — "Between 12:00 AM and 1:00 AM" through "Between 11:00 PM and 12:00 AM",
  // defaulting to 8–9 — so the end is always start + 1 and only the start is
  // stored. The window is half-open, [start, start + 1); a start of 23 runs to
  // midnight and never wraps.
  //
  // **Hours, not minutes**: the worker wakes hourly (REMINDER_TIMING.runEvery).
  //
  // **It covers invoice reminders, not lesson reminders** (#50, #87, #97), as
  // MMS's hint names auto-invoices and *invoice* reminders under Invoice
  // Settings. A lesson reminder's lead is on the event category (#87); see
  // `sendWindowState` in domain/reminders.js. The panel's help text is MMS's
  // sentence.
  sendWindowStartHour: 8,

  // There is no studio-wide switch for lesson reminders; MMS has none. "Send
  // email lesson reminders" and "Send SMS lesson reminders" are per-student,
  // on the student's own Preferences (both off on a new student), stored on
  // the student — domain/people.js, defaulting false — and domain/reminders.js
  // reads them per student per channel.
});

// Make-up credits. Both null by default: unlimited credits that never
// expire. Expiry is folded on read in domain/makeup.js; its `expire` entry is
// a hand revocation.
export const DEFAULT_MAKEUP_POLICY = Object.freeze({
  maxCredits: null,      // null = no limit
  expireAfterDays: null, // null = never expires
});

// Event scheduling. `weeklyHoldDays` is a studio setting, as MMS makes it
// ("Weekly time slot hold duration ... how many days a student's weekly time
// slot will be reserved after their original event has passed").
//
// `multiTimeZone` is MMS's "Enable Multi-Time Zone Selection — allow students
// to select a different time zone than yours. Use if you have remote students."
// Off by default, as in MMS. Turning it on puts the field on a student; the
// setting gates the control, not the data, so a zone already stored on a
// student keeps working when it is switched off.
export const DEFAULT_SCHEDULING = Object.freeze({
  checkConflicts: true,
  weeklyHoldDays: 7,
  multiTimeZone: false,
});

// ─── The record, normalised — `core/src/settings.rs` ──────────────────────
//
// Every default and bound below the vocabulary above — the text fields
// trimmed with empty as null, the naming formats and the two radios held to
// their ids, the accent colour to a six-digit hex, the whole numbers rounded
// and clamped (a balance day of 1–28, an overdue reminder of 0–365 days, an
// Email Timeframe hour of 0–23, a hold of 1–60 days), the make-up policy's
// `normalise_policy`, and a date that only looks like one refused — is
// `normalise_studio` in the core, so a phone reads a stored studio the same
// way. `firestore.rules`' `validStudio` holds the same bounds.
//
// What is here is the coercion of a stored value: a text field is `String(v)`,
// absent and null are not set; a flag is only a boolean; a number is
// `Number(v)`, with absent, null and '' not set (so 'abc' is NaN, which falls
// back); a group that is not a map has no fields.

const str = (v) => (v === null || v === undefined ? null : String(v));
const flag = (v) => (typeof v === 'boolean' ? v : null);
const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
const fieldsOf = (v) => (v && typeof v === 'object' ? v : {});

/** A stored studio document as the core's `StudioInput`. */
export function studioInput(input = {}) {
  const d = fieldsOf(input);
  const nf = fieldsOf(d.nameFormats);
  const inv = fieldsOf(d.invoicing);
  const msg = fieldsOf(d.messaging);
  const mk = fieldsOf(d.makeup);
  const sch = fieldsOf(d.scheduling);
  const mil = fieldsOf(d.mileage);
  const pol = fieldsOf(d.policies);
  const ntc = fieldsOf(d.teacherNotices);
  const vis = fieldsOf(d.portalVisibility);
  return {
    name: str(d.name),
    phone: str(d.phone),
    address: str(d.address),
    locale: str(d.locale),
    timeZone: str(d.timeZone),
    teacherFormat: str(nf.teacher),
    studentFormat: str(nf.student),
    parentFormat: str(nf.parent),
    invoiceName: str(inv.invoiceName),
    footerText: str(inv.footerText),
    accentColor: str(inv.accentColor),
    groupChargesByStudent: flag(inv.groupChargesByStudent),
    showLocation: flag(inv.showLocation),
    negativeDisplay: str(inv.negativeDisplay),
    balanceDateMode: str(inv.balanceDateMode),
    balanceDayOfMonth: num(inv.balanceDayOfMonth),
    balanceSpecificDate: str(inv.balanceSpecificDate),
    studioEmail: str(msg.studioEmail),
    useStudioNameAsSender: flag(msg.useStudioNameAsSender),
    useStudioIdentityOnReminders: flag(msg.useStudioIdentityOnReminders),
    useStudioIdentityOnLessonNotes: flag(msg.useStudioIdentityOnLessonNotes),
    sendInvoiceReminders: flag(msg.sendInvoiceReminders),
    sendInvoiceSms: flag(msg.sendInvoiceSms),
    invoiceOverdueDays: num(msg.invoiceOverdueDays),
    sendWindowStartHour: num(msg.sendWindowStartHour),
    makeupMaxCredits: num(mk.maxCredits),
    makeupExpireAfterDays: num(mk.expireAfterDays),
    checkConflicts: flag(sch.checkConflicts),
    weeklyHoldDays: num(sch.weeklyHoldDays),
    multiTimeZone: flag(sch.multiTimeZone),
    logo: str(d.logo),
    mileageAutoExpenses: flag(mil.autoExpenses),
    mileageRate: num(mil.rateTenThousandths),
    mileageExpenseCategory: str(mil.expenseCategory),
    portalBooking: flag(pol.portalBooking),
    minAdvanceHours: num(pol.minAdvanceHours),
    maxAdvanceDays: num(pol.maxAdvanceDays),
    makeupCreditsOnly: flag(pol.makeupCreditsOnly),
    portalCancellation: flag(pol.portalCancellation),
    policyText: str(pol.policyText),
    cancelDeadlineHours: num(pol.deadlineHours),
    beforeDeadlineAttendance: str(pol.beforeDeadlineAttendance),
    beforeDeadlineSlot: str(pol.beforeDeadlineSlot),
    afterDeadlineAttendance: str(pol.afterDeadlineAttendance),
    afterDeadlineSlot: str(pol.afterDeadlineSlot),
    cancelledCategoryId: str(pol.cancelledCategoryId),
    notifyPortalBooking: flag(ntc.portalBooking),
    notifyPortalCancellation: flag(ntc.portalCancellation),
    portalShowAttendees: flag(vis.allAttendees),
    portalShowTeacherContact: flag(vis.teacherEmailPhone),
    portalShowTeacherAddress: flag(vis.teacherAddress),
    portalShowMakeupCredits: flag(vis.makeupCredits),
  };
}

// The Family Portal's Visibility (#774) as the record holds it, from the
// core's flat fields.
const portalVisibilityFromCore = (s) => ({
  allAttendees: s.portalShowAttendees,
  teacherEmailPhone: s.portalShowTeacherContact,
  teacherAddress: s.portalShowTeacherAddress,
  makeupCredits: s.portalShowMakeupCredits,
});

// The Policies tab's group (#766) as the record holds it, from the core's
// flat fields.
const policiesFromCore = (s) => ({
  portalBooking: s.portalBooking,
  minAdvanceHours: s.minAdvanceHours,
  maxAdvanceDays: s.maxAdvanceDays,
  makeupCreditsOnly: s.makeupCreditsOnly,
  portalCancellation: s.portalCancellation,
  policyText: s.policyText ?? null,
  deadlineHours: s.cancelDeadlineHours,
  beforeDeadlineAttendance: s.beforeDeadlineAttendance,
  beforeDeadlineSlot: s.beforeDeadlineSlot,
  afterDeadlineAttendance: s.afterDeadlineAttendance,
  afterDeadlineSlot: s.afterDeadlineSlot,
  cancelledCategoryId: s.cancelledCategoryId ?? null,
});

/** The core's `StudioSettings` as the record every screen reads. */
export function studioFromCore(s) {
  return {
    name: s.name ?? null,
    phone: s.phone ?? null,
    address: s.address ?? null,
    locale: s.locale,
    timeZone: s.timeZone ?? null,
    nameFormats: { teacher: s.teacherFormat, student: s.studentFormat, parent: s.parentFormat },
    invoicing: {
      invoiceName: s.invoiceName ?? null,
      footerText: s.footerText ?? null,
      accentColor: s.accentColor,
      groupChargesByStudent: s.groupChargesByStudent,
      showLocation: s.showLocation,
      negativeDisplay: s.negativeDisplay,
      balanceDateMode: s.balanceDateMode,
      balanceDayOfMonth: s.balanceDayOfMonth,
      balanceSpecificDate: s.balanceSpecificDate ?? null,
    },
    messaging: {
      studioEmail: s.studioEmail ?? null,
      useStudioNameAsSender: s.useStudioNameAsSender,
      useStudioIdentityOnReminders: s.useStudioIdentityOnReminders,
      useStudioIdentityOnLessonNotes: s.useStudioIdentityOnLessonNotes,
      sendInvoiceReminders: s.sendInvoiceReminders,
      sendInvoiceSms: s.sendInvoiceSms,
      invoiceOverdueDays: s.invoiceOverdueDays,
      sendWindowStartHour: s.sendWindowStartHour,
    },
    makeup: { maxCredits: s.makeupMaxCredits ?? null, expireAfterDays: s.makeupExpireAfterDays ?? null },
    scheduling: {
      checkConflicts: s.checkConflicts,
      weeklyHoldDays: s.weeklyHoldDays,
      multiTimeZone: s.multiTimeZone,
    },
    // The studio's logo (#498), a PNG or JPEG data URI, present only when
    // there is one — `studio_fields` in core/src/settings.rs writes it on the
    // same terms. Read it as `settings.logo || null`.
    ...(s.logo ? { logo: s.logo } : {}),
    // Business Settings' Mileage Log tab (#520), on the same terms as the
    // logo: present only once the tab says something, which is when
    // `studio_fields` writes it. Read it through `mileageSettingsOf`.
    ...(s.mileageAutoExpenses || s.mileageRate || s.mileageExpenseCategory
      ? {
        mileage: {
          autoExpenses: s.mileageAutoExpenses,
          rateTenThousandths: s.mileageRate,
          expenseCategory: s.mileageExpenseCategory ?? null,
        },
      }
      : {}),
    // The Policies tab and the portal notices (#766), on the same terms:
    // present only once each says something other than MMS's defaults,
    // which is when `studio_fields` writes it. Read them through
    // `policiesOf` and `teacherNoticesOf`.
    ...(JSON.stringify(policiesFromCore(s)) !== JSON.stringify(DEFAULT_POLICIES)
      ? { policies: policiesFromCore(s) }
      : {}),
    ...(!s.notifyPortalBooking || !s.notifyPortalCancellation
      ? {
        teacherNotices: {
          portalBooking: s.notifyPortalBooking,
          portalCancellation: s.notifyPortalCancellation,
        },
      }
      : {}),
    // The Family Portal's Visibility (#774), on the same terms: present only
    // once it differs from MMS's column. Read it through `portalVisibilityOf`.
    ...(JSON.stringify(portalVisibilityFromCore(s)) !== JSON.stringify(DEFAULT_PORTAL_VISIBILITY)
      ? { portalVisibility: portalVisibilityFromCore(s) }
      : {}),
  };
}

// Business Settings' Policies tab (#766) with MMS's defaults — what a studio
// that never opened the tab has. The rules, the bounds and what MMS does are
// `core/src/settings.rs` (`POLICIES_GROUP`).
export const DEFAULT_POLICIES = Object.freeze({
  portalBooking: true,
  minAdvanceHours: 24,
  maxAdvanceDays: 60,
  makeupCreditsOnly: false,
  portalCancellation: true,
  policyText: null,
  deadlineHours: 24,
  beforeDeadlineAttendance: 'remove',
  beforeDeadlineSlot: 'keep',
  afterDeadlineAttendance: 'absent_no_credit',
  afterDeadlineSlot: 'keep',
  cancelledCategoryId: null,
});

/** A normalised record's Policies tab, its defaults filled in. */
export function policiesOf(settings) {
  return { ...DEFAULT_POLICIES, ...(settings?.policies || {}) };
}

// The teacher's two portal email notices (#766), both on, as MMS's are.
export const DEFAULT_TEACHER_NOTICES = Object.freeze({
  portalBooking: true,
  portalCancellation: true,
});

/** A normalised record's portal notices, their defaults filled in. */
export function teacherNoticesOf(settings) {
  return { ...DEFAULT_TEACHER_NOTICES, ...(settings?.teacherNotices || {}) };
}

// Business Settings → Family Portal → Visibility (#774) with MMS's values —
// what a studio that never opened the tab has, and what `familyPortalView`
// shows it by (functions/portal/view.js). Keys in MMS's order; what MMS does
// and where we differ is `PORTAL_VISIBILITY_GROUP` in core/src/settings.rs.
export const DEFAULT_PORTAL_VISIBILITY = Object.freeze({
  allAttendees: false,
  teacherEmailPhone: true,
  teacherAddress: false,
  makeupCredits: false,
});

/** A normalised record's Family Portal visibility, its defaults filled in. */
export function portalVisibilityOf(settings) {
  return { ...DEFAULT_PORTAL_VISIBILITY, ...(settings?.portalVisibility || {}) };
}

// The Mileage Log tab's group with its defaults: off, a rate of zero, no
// category — what a studio that never opened the tab has.
export const DEFAULT_MILEAGE_SETTINGS = Object.freeze({
  autoExpenses: false,
  rateTenThousandths: 0,
  expenseCategory: null,
});

/** A normalised record's Mileage Log tab, its defaults filled in. */
export function mileageSettingsOf(settings) {
  return { ...DEFAULT_MILEAGE_SETTINGS, ...(settings?.mileage || {}) };
}

/** A normalised record as the core's `StudioSettings`. */
export function studioToCore(s) {
  return {
    name: s.name ?? null,
    phone: s.phone ?? null,
    address: s.address ?? null,
    locale: s.locale,
    timeZone: s.timeZone ?? null,
    teacherFormat: s.nameFormats.teacher,
    studentFormat: s.nameFormats.student,
    parentFormat: s.nameFormats.parent,
    ...s.invoicing,
    ...s.messaging,
    makeupMaxCredits: s.makeup.maxCredits ?? null,
    makeupExpireAfterDays: s.makeup.expireAfterDays ?? null,
    ...s.scheduling,
    logo: s.logo ?? null,
    mileageAutoExpenses: s.mileage?.autoExpenses ?? false,
    mileageRate: s.mileage?.rateTenThousandths ?? 0,
    mileageExpenseCategory: s.mileage?.expenseCategory ?? null,
    // Every one named, because the wasm record's missing field is its type's
    // zero rather than MMS's default (core-ffi/src/studio.rs).
    ...(() => {
      const p = policiesOf(s);
      const n = teacherNoticesOf(s);
      const v = portalVisibilityOf(s);
      return {
        portalBooking: p.portalBooking,
        minAdvanceHours: p.minAdvanceHours,
        maxAdvanceDays: p.maxAdvanceDays,
        makeupCreditsOnly: p.makeupCreditsOnly,
        portalCancellation: p.portalCancellation,
        policyText: p.policyText ?? null,
        cancelDeadlineHours: p.deadlineHours,
        beforeDeadlineAttendance: p.beforeDeadlineAttendance,
        beforeDeadlineSlot: p.beforeDeadlineSlot,
        afterDeadlineAttendance: p.afterDeadlineAttendance,
        afterDeadlineSlot: p.afterDeadlineSlot,
        cancelledCategoryId: p.cancelledCategoryId ?? null,
        notifyPortalBooking: n.portalBooking,
        notifyPortalCancellation: n.portalCancellation,
        portalShowAttendees: v.allAttendees,
        portalShowTeacherContact: v.teacherEmailPhone,
        portalShowTeacherAddress: v.teacherAddress,
        portalShowMakeupCredits: v.makeupCredits,
      };
    })(),
  };
}

// The fields the studio document holds, every default applied. A missing
// document and an empty one are the same studio to everything above here.
export function normaliseStudio(input = {}) {
  return studioFromCore(coreSync().normaliseStudio(studioInput(input)));
}

export function normaliseNameFormats(input = {}) {
  return normaliseStudio({ nameFormats: input }).nameFormats;
}

export function normaliseInvoicing(input = {}) {
  return normaliseStudio({ invoicing: input }).invoicing;
}

export function normaliseMessaging(input = {}) {
  return normaliseStudio({ messaging: input }).messaging;
}

// The make-up policy: `normalise_policy` in core/src/makeup.rs, through the
// studio record like its neighbours.
export function normaliseMakeupPolicy(input = {}) {
  return normaliseStudio({ makeup: input }).makeup;
}

export function normaliseScheduling(input = {}) {
  return normaliseStudio({ scheduling: input }).scheduling;
}

// What to print at the top of an invoice, a report or a receipt.
export function studioDisplayName(studio, fallback = UNNAMED_STUDIO) {
  return coreSync().studioDisplayName(str(studio?.name), str(fallback));
}

// Whether the studio has told us what it is called — derived rather than
// stored, for the reason domain/onboarding.js gives.
export function isStudioNamed(studio) {
  return coreSync().isStudioNamed(str(studio?.name));
}

// ─── Naming format, applied — `core/src/names.rs` (#214) ───────────────────
//
// The *vocabulary* is a studio setting, and people.js and notifications.js
// both render names and must render them the same way, so the formatting rule
// lives in **one** place: `names.rs` is the only implementation, reached from
// the browser through `mod web` and from Kotlin through `mod native`. The two
// rules it encodes:
//
//   * a person with only one of the two names is written as that name, in
//     every format — "Okafor," is not a name, and neither is ", Ada";
//   * the sort key follows the format but is not derived from the display
//     name, because the comma would sort between letters.
//
// **What is here is the vocabulary itself** — `NAME_FORMATS`,
// `NAME_FORMAT_OPTIONS`, `DEFAULT_NAME_FORMATS` and `normaliseNameFormats`:
// the ids a stored setting holds and the shape of the document that holds
// them. `names.rs` carries its own copy of the three ids because it matches on
// them, which lets the core decide without asking the caller what it meant.
//
// `NamingFormatPanel` and `notifications.js` reach `coreSync()` directly.
